import { useEffect, useMemo, useState } from "react";

import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { App, Flex, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useLazyGetOrganizationByIdQuery,
  useLazyGetSectionsQuery,
  useLazyGetSurveyByIdQuery,
  useLazyGetSurveyStatisticsQuery,
  useLazyGetSurveyViewByIdQuery,
  useLazyIncrementSurveyViewViewsQuery,
} from "@/redux/service.js";
import { Roles, SharingRights } from "@/utilities/constants.jsx";
import SurveyViewDetails from "./SurveyViewDetails.jsx";
import BaseButton from "@/components_v2/Button/BaseButton.js";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer.js";
import AddDocumentIcon from "@/assets/icons/add-document.svg?react";
import DownloadSurvey from "@/views/survey/components/DownloadSurvey.jsx";
import { DownloadOutlined, ShareAltOutlined } from "@ant-design/icons";

export class InsufficientPermissions extends Error {
  constructor(message = "", navigateTo = null) {
    super(message);
    this.message = message;
    this.navigateTo = navigateTo;
  }
}

const SurveyView = () => {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const location = useLocation();

  const [getSurveyById, surveyResponse] = useLazyGetSurveyByIdQuery();
  const [getSurveyViewById, surveyViewResponse] =
    useLazyGetSurveyViewByIdQuery();
  const [getCurrentUser, { isLoading: isCurrentUserLoading }] =
    useLazyGetCurrentUserQuery();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [getSections, sectionsResponse] = useLazyGetSectionsQuery();
  const [getSurveyStatics, surveyStatisticsResponse] =
    useLazyGetSurveyStatisticsQuery();
  const [incrementSurveyViewViews] = useLazyIncrementSurveyViewViewsQuery();
  const [getOrganizationById, organizationResponse] =
    useLazyGetOrganizationByIdQuery();
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const { data: currentUser } = useGetCurrentUserQuery();

  const getSurveyViewData = async () => {
    const sections = [];
    const questions = [];
    const response = await getSurveyViewById(searchParams.get("view"));
    if (
      response.data.sharingRights !== SharingRights.PUBLIC.value &&
      !isAuthenticated
    ) {
      throw new InsufficientPermissions(t("surveyViewNotPublic"), "/login");
    }

    (response.data?.answers || []).forEach((i) => {
      sections.push(i.section);
      questions.push(i.question);
    });
    const organization = response.data.organization.id;

    return { organization, sections, questions };
  };

  const getSurveyData = async () => {
    /**
     * Retrieves the survey data along with the survey view, if specified.
     * The view parameter must be present if the user tries to look up a report of a different organization. If
     * it's not specified, the data of the users' organization will be returned.
     */
    let filters = { organization: null, sections: [], questions: null };
    if (searchParams.has("view")) {
      filters = await getSurveyViewData();
    } else {
      if (!isAuthenticated) {
        throw new InsufficientPermissions(t("viewRequired"), "/login");
      }

      if (location.pathname.includes("public")) {
        navigate(`/reports/${surveyId}/report${location.search}`, {
          replace: true,
        });
      }

      const response = await getCurrentUser();
      if (response.isSuccess) {
        if (response.data.role === Roles.ADMIN.value) {
          if (!searchParams.has("organization")) {
            throw new InsufficientPermissions(
              t("organizationRequiredParameter"),
              "/",
            );
          }
          filters.organization = searchParams.get("organization");
        } else {
          filters.organization = response.data.organization.id;
        }
      }
    }

    getSurveyById({
      surveyId,
      filters: { organization: filters.organization },
    });
    getSections({
      ids: filters.sections,
      survey: surveyId,
      isTab: false,
      pagination: "off",
    });
    setFilteredQuestions(filters.questions);
    getSurveyStatics({
      survey: surveyId,
      organization: filters.organization,
    });
    if (filters.organization) {
      getOrganizationById(filters.organization);
    }
  };

  const retrieveData = () => {
    getSurveyData().catch((err) => {
      if (err instanceof InsufficientPermissions) {
        notification.error({ message: err.message, key: err.message });
        navigate(err.navigateTo);
        return;
      }
      throw err;
    });
  };

  useEffect(() => {
    retrieveData();

    if (isAuthenticated && searchParams.has("view")) {
      setTimeout(() => {
        incrementSurveyViewViews(searchParams.get("view"));
      }, 15000);
    }
  }, [isAuthenticated]);

  const organizationName = useMemo(() => {
    if (organizationResponse.isSuccess) {
      return organizationResponse.data.name;
    }
    if (surveyViewResponse.isSuccess) {
      return surveyViewResponse.data.organization.title;
    }
    return null;
  }, [organizationResponse]);

  if (surveyResponse.isLoading || isCurrentUserLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <Spin size="large" />
      </div>
    );
  }

  if (surveyResponse.isError) {
    return <Navigate to="/" />;
  }

  return surveyResponse.isSuccess ? (
    <Flex vertical gap={"large"}>
      <Flex justify="space-between" wrap gap={"small"}>
        <h1 className="header-l-medium">
          {organizationName} - {surveyResponse.data.title}
        </h1>

        <div className="survey-view-header-buttons">
          {surveyViewResponse.isSuccess && (
            <BaseButton
              variant="tertiary"
              leadingIcon={<ShareAltOutlined />}
              onClick={() =>
                navigate("/discussions/form", {
                  state: { surveyView: surveyViewResponse.data },
                })
              }
            >
              {t("buttons.shareToCommunity")}
            </BaseButton>
          )}

          {currentUser &&
            organizationResponse.data &&
            currentUser.organization?.id === organizationResponse.data.id &&
            surveyResponse.data.createdAt.includes("2024") && (
              <>
                <BaseButton
                  variant="tertiary"
                  onClick={() => navigate(`/reports/${surveyId}/views/form`)}
                  leadingIcon={
                    <BaseSvgRenderer
                      width="20"
                      height="20"
                      component={AddDocumentIcon}
                    />
                  }
                >
                  {t("buttons.generateViews")}
                </BaseButton>
                <DownloadSurvey
                  survey={surveyResponse.data}
                  filters={{
                    view: searchParams.get("view"),
                    organization: searchParams.get("organization"),
                  }}
                >
                  <BaseButton leadingIcon={<DownloadOutlined />}>
                    {t("buttons.downloadReport")}
                  </BaseButton>
                </DownloadSurvey>
              </>
            )}
        </div>
      </Flex>

      {sectionsResponse.isSuccess && (
        <SurveyViewDetails
          sections={sectionsResponse.data}
          surveyStatisticsResponse={surveyStatisticsResponse}
          surveyViewResponse={surveyViewResponse}
          filteredQuestions={filteredQuestions}
        />
      )}
    </Flex>
  ) : null;
};

export default SurveyView;
